/**
 * WhoWeAreSignatureController — WHO WE ARE §3 시그니처 시퀀스.
 *
 * 데스크톱(≥769 · 모션ON): __stage 핀 고정. 시작상태 = 중앙에 카드 3장 파일(위 카드가 앞).
 *   스크롤 진행도(progress) 매핑:
 *   A) 0 → 0.54  카드가 하나씩 위로 떠오르며(rise up) 페이드아웃 → 다음 카드 노출.
 *   C) 0.60→1.00 배경 워드마크 scale 2.6→1 축소 + 골드(gold-background.jpg) text-clip
 *      리빌(.is-revealed) + Contact CTA 등장.
 * 모바일/모션OFF: 아무것도 하지 않음 → SCSS 기본 세로 흐름(그레이스풀).
 *
 * ⚠ rest offset(-12px, -6px, scale-0.04, rotate -12/7/15)·START_SCALE(2.6) 는 _sec-wwa-signature.scss 와 동기.
 */
import { getGsap, getScrollTrigger } from "../global/GsapGlobals";

const ROOT_SEL = '[data-section="wwa-signature"]';

function clamp(v: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(v, hi));
}

export const WhoWeAreSignatureController = {
    init(): void {
        const root = document.querySelector<HTMLElement>(ROOT_SEL);
        if (!root) return;
        if (root.dataset.wwaSigBound === "true") return;
        root.dataset.wwaSigBound = "true";

        const stage = root.querySelector<HTMLElement>(".sec-wwa-signature__stage");
        const head = root.querySelector<HTMLElement>(".sec-wwa-signature__head");
        const wordmark = root.querySelector<HTMLElement>("[data-wordmark]");
        const cards = Array.from(root.querySelectorAll<HTMLElement>("[data-card]"));
        const cta = root.querySelector<HTMLElement>("[data-sig-cta]");
        if (!stage || !wordmark || cards.length < 1) return;

        const gsap = getGsap();
        const ScrollTrigger = getScrollTrigger();

        // ── 튜너블 (SCSS 와 동기) ──
        const START_SCALE = 2.6;
        const CARD_EXIT_DUR = 0.18; // 각 카드 퇴장 구간 길이
        const CARD_STAGGER = 0.18; // 카드별 퇴장 시작 간격 (0/0.18/0.36 → 0.54 종료)
        const WORD_START = 0.6; // 워드마크 축소 시작
        const REVEAL_AT = 0.55; // 워드마크 로컬 진행도(골드 on)
        const CARD_ROTATIONS = [-12, 7, 15];

        const restOf = (i: number) => ({
            x: -i * 6,
            y: -i * 12,
            scale: 1 - i * 0.04,
            rotate: CARD_ROTATIONS[i] ?? 0,
        });

        const mm = gsap.matchMedia();
        mm.add(
            "(min-width: 769px) and (prefers-reduced-motion: no-preference)",
            () => {
                root.classList.add("is-immersive");

                // 초기: 카드 3장 중앙 파일(가시), 워드마크 대형 다크(SCSS).
                cards.forEach((card, i) => {
                    const r = restOf(i);
                    gsap.set(card, {
                        x: r.x,
                        y: r.y,
                        scale: r.scale,
                        rotate: r.rotate,
                        opacity: 1,
                    });
                });

                const st = ScrollTrigger.create({
                    trigger: stage,
                    start: "top top",
                    end: () => `+=${window.innerHeight * 3}`,
                    pin: true,
                    pinSpacing: true,
                    scrub: 1,
                    invalidateOnRefresh: true,
                    onUpdate: (self) => {
                        const p = self.progress;

                        // ── BAND A: 카드가 위로 하나씩 떠오르며 퇴장 ──
                        cards.forEach((card, i) => {
                            const r = restOf(i);
                            const e = clamp(
                                (p - i * CARD_STAGGER) / CARD_EXIT_DUR,
                                0,
                                1,
                            );
                            const y = gsap.utils.interpolate(
                                r.y,
                                -window.innerHeight * 0.85,
                                e,
                            );
                            const sc = gsap.utils.interpolate(r.scale, r.scale + 0.06, e);
                            gsap.set(card, {
                                x: r.x,
                                y,
                                scale: sc,
                                rotate: gsap.utils.interpolate(r.rotate, 0, e),
                                opacity: 1 - e,
                            });
                        });

                        // ── BAND C: 워드마크 축소 + 골드 리빌 + CTA ──
                        const wmP = clamp((p - WORD_START) / (1 - WORD_START), 0, 1);
                        gsap.set(wordmark, {
                            scale: gsap.utils.interpolate(START_SCALE, 1, wmP),
                        });
                        wordmark.classList.toggle("is-revealed", wmP > REVEAL_AT);

                        if (head) {
                            gsap.set(head, {
                                y: gsap.utils.interpolate(0, 34, wmP),
                            });
                        }

                        if (cta) {
                            const ctaP = clamp((wmP - 0.6) / 0.4, 0, 1);
                            gsap.set(cta, {
                                opacity: ctaP,
                                y: gsap.utils.interpolate(24, -34, wmP),
                            });
                        }
                    },
                });

                return () => {
                    st.kill();
                    root.classList.remove("is-immersive");
                    wordmark.classList.remove("is-revealed");
                    gsap.set([wordmark, head, cta, ...cards].filter(Boolean), {
                        clearProps: "all",
                    });
                };
            },
        );

        if (document.readyState === "complete") {
            ScrollTrigger.refresh();
        } else {
            window.addEventListener("load", () => ScrollTrigger.refresh(), {
                once: true,
            });
        }
    },
};
